import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Speed: find any of the three
export default function SpeedCalc() {
  const [mode, setMode] = useState('speed'); // speed | distance | time | conv
  const [distance, setDistance] = useState(100);
  const [distUnit, setDistUnit] = useState('km');
  const [hours, setHours] = useState(1);
  const [minutes, setMinutes] = useState(30);
  const [seconds, setSeconds] = useState(0);
  const [speed, setSpeed] = useState(60);
  const [speedUnit, setSpeedUnit] = useState('mph');

  const distToKm = (v,u) => ({ km:v, mi:v*1.60934, m:v/1000, ft:v/3280.84, yd:v/1093.61 })[u];
  // Speed unit conversions to km/h base
  const SPEED_UNITS = {
    'km/h':      { toKmh: 1,        label: 'km/h — kilometers per hour' },
    'mph':       { toKmh: 1.60934,  label: 'mph — miles per hour' },
    'm/s':       { toKmh: 3.6,      label: 'm/s — meters per second' },
    'knots':     { toKmh: 1.852,    label: 'knots — nautical miles per hour' },
    'ft/s':      { toKmh: 1.09728,  label: 'ft/s — feet per second' },
    'ft/min':    { toKmh: 0.018288, label: 'ft/min — feet per minute' },
    'in/s':      { toKmh: 0.09144,  label: 'in/s — inches per second' },
    'cm/s':      { toKmh: 0.036,    label: 'cm/s — centimeters per second' },
    'mm/s':      { toKmh: 0.0036,   label: 'mm/s — millimeters per second' },
    'yd/s':      { toKmh: 3.29184,  label: 'yd/s — yards per second' },
    'mi/s':      { toKmh: 5793.64,  label: 'mi/s — miles per second' },
    'mi/min':    { toKmh: 96.5606,  label: 'mi/min — miles per minute' },
    'km/s':      { toKmh: 3600,     label: 'km/s — kilometers per second' },
    'km/min':    { toKmh: 60,       label: 'km/min — kilometers per minute' },
    'mach':      { toKmh: 1234.8,   label: 'mach — speed of sound (@sea level)' },
    'c':         { toKmh: 1.079252848e9, label: 'c — speed of light in vacuum' },
    'furlong/fortnight': { toKmh: 6.0480e-4, label: 'furlong / fortnight (fun)' },
  };
  const speedToKmh = (v, u) => Number(v) * (SPEED_UNITS[u]?.toKmh || 1);
  const kmhTo = (v, u) => Number(v) / (SPEED_UNITS[u]?.toKmh || 1);

  const totalHours = Number(hours) + Number(minutes)/60 + Number(seconds)/3600;

  const result = useMemo(() => {
    if (mode === 'speed') {
      const km = distToKm(Number(distance), distUnit);
      if (!totalHours) return null;
      const kmh = km / totalHours;
      return { value: kmhTo(kmh, speedUnit).toFixed(3), label: `Speed (${speedUnit})` };
    }
    if (mode === 'distance') {
      const kmh = speedToKmh(Number(speed), speedUnit);
      const km = kmh * totalHours;
      const map = { km, mi: km/1.60934, m: km*1000, ft: km*3280.84, yd: km*1093.61 };
      return { value: map[distUnit].toFixed(3), label: `Distance (${distUnit})` };
    }
    if (mode === 'time') {
      const km = distToKm(Number(distance), distUnit);
      const kmh = speedToKmh(Number(speed), speedUnit);
      if (!kmh) return null;
      const h = km / kmh;
      const H = Math.floor(h); const M = Math.floor((h - H) * 60); const S = Math.round((((h - H) * 60) - M) * 60);
      return { value: `${H}h ${M}m ${S}s`, label: 'Time' };
    }
    // conversion
    const kmh = speedToKmh(Number(speed), speedUnit);
    return null;
  }, [mode, distance, distUnit, totalHours, speed, speedUnit]);

  const converted = Object.keys(SPEED_UNITS).map(u => ({ u, label: SPEED_UNITS[u].label, v: kmhTo(speedToKmh(Number(speed), speedUnit), u) }));

  return (
    <CalcShell look="cnet" title="Speed Calculator" subtitle="Compute speed, distance or time — or convert between common speed units.">
      <div className="flex gap-2 border-b mb-4 flex-wrap">
        {[['speed','Find Speed'],['distance','Find Distance'],['time','Find Time'],['conv','Speed Converter']].map(([v,l]) => (
          <button key={v} className={`px-4 py-2 text-sm font-semibold ${mode===v?'border-b-2 border-blue-600 text-blue-700':'text-slate-500'}`} onClick={() => setMode(v)}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          {mode !== 'conv' && mode !== 'distance' && (
            <div className="field"><label>Distance</label>
              <div className="flex gap-2">
                <input type="number" value={distance} onChange={e => setDistance(e.target.value)} className="flex-1"/>
                <select value={distUnit} onChange={e => setDistUnit(e.target.value)}><option>km</option><option>mi</option><option>m</option><option>ft</option><option>yd</option></select>
              </div>
            </div>
          )}
          {mode !== 'conv' && mode !== 'time' && mode !== 'speed' && (
            <div className="field"><label>Speed</label>
              <div className="flex gap-2">
                <input type="number" value={speed} onChange={e => setSpeed(e.target.value)} className="flex-1"/>
                <select value={speedUnit} onChange={e => setSpeedUnit(e.target.value)}>{Object.entries(SPEED_UNITS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
              </div>
            </div>
          )}
          {mode === 'time' && (
            <div className="field"><label>Speed</label>
              <div className="flex gap-2">
                <input type="number" value={speed} onChange={e => setSpeed(e.target.value)} className="flex-1"/>
                <select value={speedUnit} onChange={e => setSpeedUnit(e.target.value)}>{Object.entries(SPEED_UNITS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
              </div>
            </div>
          )}
          {mode === 'distance' && (
            <div className="field"><label>Speed</label>
              <div className="flex gap-2">
                <input type="number" value={speed} onChange={e => setSpeed(e.target.value)} className="flex-1"/>
                <select value={speedUnit} onChange={e => setSpeedUnit(e.target.value)}>{Object.entries(SPEED_UNITS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
              </div>
            </div>
          )}
          {(mode === 'speed' || mode === 'distance') && (
            <div>
              <label className="text-sm font-semibold text-slate-700">Time</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <input type="number" value={hours} onChange={e => setHours(e.target.value)} placeholder="h" className="input"/>
                <input type="number" value={minutes} onChange={e => setMinutes(e.target.value)} placeholder="m" className="input"/>
                <input type="number" value={seconds} onChange={e => setSeconds(e.target.value)} placeholder="s" className="input"/>
              </div>
            </div>
          )}
          {mode === 'conv' && (
            <div className="field"><label>Enter Speed</label>
              <div className="flex gap-2">
                <input type="number" value={speed} onChange={e => setSpeed(e.target.value)} className="flex-1"/>
                <select value={speedUnit} onChange={e => setSpeedUnit(e.target.value)}>{Object.entries(SPEED_UNITS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
              </div>
            </div>
          )}
          {mode === 'distance' && (
            <div>
              <label className="text-sm font-semibold text-slate-700">Time</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <input type="number" value={hours} onChange={e => setHours(e.target.value)} placeholder="h" className="input"/>
                <input type="number" value={minutes} onChange={e => setMinutes(e.target.value)} placeholder="m" className="input"/>
                <input type="number" value={seconds} onChange={e => setSeconds(e.target.value)} placeholder="s" className="input"/>
              </div>
              <div className="field mt-3"><label>Output distance unit</label>
                <select value={distUnit} onChange={e => setDistUnit(e.target.value)}><option>km</option><option>mi</option><option>m</option><option>ft</option><option>yd</option></select>
              </div>
            </div>
          )}
        </div>
        <div>
          {mode !== 'conv' ? result && (
            <div className="p-5 rounded-xl bg-blue-50 border border-blue-200">
              <div className="text-sm text-slate-600">{result.label}</div>
              <div className="text-4xl font-bold text-blue-700" data-testid="speed-result">{result.value}</div>
            </div>
          ) : (
            <table className="data-table">
              <thead><tr><th>Unit</th><th className="text-right">Value</th></tr></thead>
              <tbody>{converted.map(x => (
                <tr key={x.u}>
                  <td className="pr-3">{x.label}</td>
                  <td className="text-right font-mono">{x.v < 1e-4 || x.v > 1e10 ? x.v.toExponential(6) : x.v.toFixed(x.v < 1 ? 8 : 4)}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>
    </CalcShell>
  );
}
